import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { injectMutation, injectQuery, injectQueryClient } from './query'
import { QueryObserverResult } from '@tanstack/query-core'
import { map, Observable, tap } from 'rxjs'
import {
  MinutesVerboseness,
  MinutesAnonymization,
  MinutesPassive,
  MinutesNameFormat,
  MinutesDateFormat,
  MinutesTimeFormat,
  MinutesLanguage,
  MinutesAttribution,
  AdminCallDetails,
  AgendaTranscriptMappingMode,
  CallMedium,
  MinutesJobVersion,
  MinutesStyling,
  MinutingAgent,
} from './calls.service'
import { DownloadsService } from './downloads.service'
import { ListParameterQuery } from './list-query-options'
import { queryKeys } from './query-keys'
import { ToastService } from './toast.service'
import { AdminListUser, User } from './users.service'
import { generateHeadersWithTimezone } from './utils'
import { ResolvedSettings, ClientSettings, EncryptionKeySource } from './client-settings'

export type EnterpriseOidcProviderPlatform = 'AZURE' | 'OKTA' | 'GOOGLE' | 'OTHER'
export type EnterpriseOidcProviderType = 'AUTH' | 'CALENDAR' | 'AUTH_AND_CALENDAR'
export type GuestOrganization = {
  id: number
  name: string
  domains: string[]
}

export type EnterpriseOidcProvider = {
  platform: EnterpriseOidcProviderPlatform
  type: EnterpriseOidcProviderType
  keycloakHint: string
}

export type OrganizationClient = {
  id: number
  name: string
  isInternal: boolean
} & ClientSettings

export type Organization = {
  id: number
  name: string
  clientId: number
  client: OrganizationClient
  // What the assistant is actually called on this organization's calls, computed and sanitized
  // server-side (see resolveAssistantDisplayName in the backend): the organization's own override,
  // else the client's assistantDisplayName, else the client's name with " Assistant" appended.
  resolvedAssistantDisplayName: string
  canBeSupport: boolean
  shareUrlOptOut: boolean | null
  transcriberInputLanguage: string | null
  transcriberOutputLanguage: string | null
  allowTranscriptDownload: boolean | null
  allowVideoDownload: boolean | null
  allowMinutesDownload: boolean | null
  allowOutlineDownload: boolean | null
  allowManualCallCreation: boolean | null
  allowCallUpload: boolean | null
  isIntercomEnabled: boolean | null
  isSharingEnabled: boolean | null
  isCalendarSynchronizationEnabled: boolean | null
  allowSignUp: boolean | null
  llmModelOverride: string | null
  encryptionKeySourceOverride: EncryptionKeySource | null
  useVerificationDiscovery: boolean | null
  anonymiseMemberSpeakers: boolean | null
  useAgendaTranscriptMapping: AgendaTranscriptMappingMode | null
  minutesJobVersion: MinutesJobVersion | null
  allowBoardPackUploads: boolean | null
  allowAdminCallActions: boolean | null
  allowRetentionExtensionRequests: boolean | null
  recordingType: 'VIDEO' | 'AUDIO' | null
  retentionPeriodInDaysForMediaOverride: number | null
  retentionPeriodInDaysForDocumentsOverride: number | null
  retentionPeriodInDaysForTranscriptOverride: number | null
  retentionPeriodInDaysForCallOverride: number | null
  maxSingleRetentionExtensionInDaysOverride: number | null
  maxTotalRetentionExtensionInDaysOverride: number | null
  softDeletionPeriodInDays: number | null
  assistantDisplayNameOverride: string | null
  domains: string[]
  microsoftTenantIds: string[]
  enterpriseOidcProviders: EnterpriseOidcProvider[]
  contactEmails: string[]
  organizationType: string
  deactivated: boolean
  createdAt: Date
  defaultCallMedium: CallMedium | null
  sendSoftDeletionWarningEmail: boolean | null
  softDeletionWarningHours: number | null
  sendSoftDeletionConfirmationEmail: boolean | null
  sendHardDeletionConfirmationEmail: boolean | null
  sendAssistantAccessDeniedEmail: boolean | null
  sendUpcomingCallReminderEmail: boolean | null
  sendWeeklyDigestEmail: boolean | null
  sendConsentNotification: boolean | null
  joinMeetingInternal: boolean | null
  joinMeetingExternal: boolean | null
  freezeJoinSettings: boolean | null
  useAuthenticatedBot: boolean
  useNativeBot: boolean
  useSectionHeadings: boolean | null
  usePartialMinutesRegeneration: boolean | null
  sessionTimeoutInMinutes: number | null
  defaultCallProfileId: number | null
  context: string[]
  scimKey: string | null
  // The minutes styling settings are organization-level overrides of the client defaults.
  minutesVerboseness: MinutesVerboseness | null
  minutesAnonymization: MinutesAnonymization | null
  minutesPassive: MinutesPassive | null
  minutesNameFormat: MinutesNameFormat | null
  minutesDateFormat: MinutesDateFormat | null
  minutesTimeFormat: MinutesTimeFormat | null
  minutesLanguage: MinutesLanguage | null
  minutesAttribution: MinutesAttribution | null
}

export type OrganizationAudioMetrics = {
  avgWhisperScore: number | null
  whisperScoreVariance: number | null
  avgRms: number | null
  avgHfRatio: number | null
  avgWhisperScoreLast10: number | null
  avgRmsLast10: number | null
  avgHfRatioLast10: number | null
}

export type OrganizationDetails = Organization & {
  users: User[]
  // What actually applies once the client defaults and this organization's overrides are combined.
  resolvedSettings: ResolvedSettings
}

// A new organization may be created without picking a client, in which case the backend gives it a
// client of its own named after the organization. It also has no resolvedAssistantDisplayName yet —
// that field is computed server-side, and nothing has been saved to compute it from.
export type OrganizationToBeCreated = Omit<Organization, 'id' | 'createdAt' | 'clientId' | 'client' | 'resolvedAssistantDisplayName'> & {
  clientId: number | null
}

export type OrganizationQueryResult = {
  list: (Organization & { userCount: number })[]
  total: number
}

export type OrganizationUpdateData = {
  organization: Organization
}

export type OrganizationFeature = 'AGENDA_TRANSCRIPT_MAPPING' | 'BOARD_PACK_UPLOADS' | 'PARTIAL_MINUTES_REGENERATION'

export type SetOrganizationFeature = {
  organizationIds: number[]
  feature: OrganizationFeature
  enabled: boolean
}

export type OrganizationCreateData = {
  organization: OrganizationToBeCreated
}

export type UsageEntry = {
  totalCallDurationInSeconds: number
  totalCallCount: number
}

export type OrganizationUsage = {
  label: string
  usage: UsageEntry
}[]

// The whole organization row, so the view page can report each setting's own value next to the
// client's and the effective one.
export type AdminOrganizationDetails = Organization & {
  // What actually applies once the client defaults and this organization's overrides are combined.
  resolvedSettings: ResolvedSettings
} & {
  pastCalls: AdminCallDetails[]
  upcomingCalls: AdminCallDetails[]
  erroredCalls: AdminCallDetails[]
  usage: OrganizationUsage
  users: AdminListUser[]
  guestOrganization: GuestOrganization
}

@Injectable({
  providedIn: 'root',
})
export class OrganizationsService {
  httpClient = inject(HttpClient)
  query = injectQuery()
  mutation = injectMutation()
  queryClient = injectQueryClient()
  toastService = inject(ToastService)
  router = inject(Router)
  downloadsService = inject(DownloadsService)

  getAllOrganizations(query: string | null = null, sortField: string | null = null, sortDirection: 'asc' | 'desc' | null = null, clientIds: number[] = []) {
    const params: { query?: string; sortField?: string; sortDirection?: 'asc' | 'desc'; clientIds?: number[] } = {}
    if (query !== null) {
      params.query = query
    }
    if (sortField !== null) {
      params.sortField = sortField
    }
    if (sortDirection !== null) {
      params.sortDirection = sortDirection
    }
    if (clientIds.length > 0) {
      params.clientIds = clientIds
    }
    return this.query({
      queryKey: [...queryKeys.organizations.all, query, sortField, sortDirection, clientIds],
      queryFn: () =>
        this.httpClient.get<(Organization & { userCount: number } & Partial<OrganizationAudioMetrics>)[]>('/api/organizations', {
          params,
        }),
    }).result$
  }

  getOrganizations(page: number, pageSize: number, sortField?: string, sortDirection?: 'asc' | 'desc') {
    const params: ListParameterQuery = { page, pageSize }
    if (sortField) {
      params.sortField = sortField
      params.sortDirection = sortDirection
    }

    return this.query({
      queryKey: queryKeys.organizations.base,
      queryFn: () => this.httpClient.get<OrganizationQueryResult>('/api/organizations/support', { params }),
    }).result$
  }

  getOrganization(id: number, receiveUpdates = false) {
    return this.query({
      queryKey: queryKeys.organizations.byId(id),
      queryFn: () => this.httpClient.get<OrganizationDetails>(`/api/organizations/${id}`),
      refetchOnMount: receiveUpdates,
      refetchOnReconnect: receiveUpdates,
      refetchOnWindowFocus: receiveUpdates,
    }).result$
  }

  createOrganization() {
    return this.mutation({
      mutationFn: (organizationCreateDto: OrganizationCreateData) => this.httpClient.post<Organization>('/api/organizations', organizationCreateDto),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.organizations.base }),
      onError: () => {
        this.toastService.show('ORGANIZATIONS.CREATE_FAILED')
      },
    })
  }

  updateOrganization() {
    return this.mutation({
      mutationFn: (organizationUpdateDto: OrganizationUpdateData) =>
        this.httpClient.patch<Organization>(`/api/organizations/${organizationUpdateDto.organization.id}`, organizationUpdateDto),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.organizations.base }),
      onError: () => {
        this.toastService.show('ORGANIZATIONS.EDIT_FAILED')
      },
    })
  }

  refreshMicrosoftTenantIds() {
    return this.mutation({
      mutationFn: (organizationId: number) => this.httpClient.post<Organization>(`/api/organizations/${organizationId}/refresh-microsoft-tenant-ids`, {}),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.organizations.base }),
      onError: () => {
        this.toastService.show('ORGANIZATIONS.REFRESH_MICROSOFT_TENANT_IDS_FAILED')
      },
    })
  }

  setOrganizationFeature() {
    return this.mutation({
      mutationFn: (setOrganizationFeatureDto: SetOrganizationFeature) => this.httpClient.patch<void>('/api/organizations/features', setOrganizationFeatureDto),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.organizations.base }),
      onError: () => {
        this.toastService.show('ORGANIZATIONS.ADMIN.FEATURES.UPDATE_FAILED')
      },
    })
  }

  deleteOrganization() {
    return this.mutation({
      mutationFn: (id: number) => this.httpClient.delete<void>(`/api/organizations/${id}`),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.organizations.base })
      },
      onError: () => {
        this.toastService.show('ORGANIZATIONS.ADMIN.DELETE_FAILED')
      },
    })
  }

  activateAllUsers() {
    return this.mutation({
      mutationFn: ({ organizationId, notify }: { organizationId: number; notify: boolean }) =>
        this.httpClient.post<number>(`/api/organizations/${organizationId}/activate-all-users`, { notify }),
      onSuccess: (_, { organizationId }) => this.queryClient.invalidateQueries({ queryKey: queryKeys.organizations.byId(organizationId) }),
      onError: () => {
        this.toastService.show('ORGANIZATIONS.ADMIN.ACTIVATE_ALL_USERS_FAILED')
      },
    })
  }

  deactivateAllUsers() {
    return this.mutation({
      mutationFn: ({ organizationId, notify }: { organizationId: number; notify: boolean }) =>
        this.httpClient.post<number>(`/api/organizations/${organizationId}/deactivate-all-users`, { notify }),
      onSuccess: (_, { organizationId }) => this.queryClient.invalidateQueries({ queryKey: queryKeys.organizations.byId(organizationId) }),
      onError: () => {
        this.toastService.show('ORGANIZATIONS.ADMIN.DEACTIVATE_ALL_USERS_FAILED')
      },
    })
  }

  getAdminViewToOrganization(id: number): Observable<QueryObserverResult<AdminOrganizationDetails>> {
    return this.query({
      queryKey: queryKeys.organizations.supportById(id),
      queryFn: () => this.httpClient.get<AdminOrganizationDetails>(`/api/organizations/${id}/support`),
      refetchInterval: 30000,
    }).result$
  }

  downloadUsageReport(organization: AdminOrganizationDetails, fromDate: Date, toDate: Date): Observable<void> {
    const fileName = `usage-report-${organization.name}-${fromDate.toISOString()}-${toDate.toISOString()}.csv`
    return this.httpClient
      .get(`/api/organizations/${organization.id}/usage-report`, {
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
        params: {
          startDate: fromDate.toISOString(),
          endDate: toDate.toISOString(),
        },
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, 'csv')
        }),
        map(() => void 0)
      )
  }
}
